import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatRadioChange } from '@angular/material/radio';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CountyListResponse, County } from '@ptl/global-components';
import { CountyListService } from '@ptl/global-components';
import { NormalizedResponse } from '@ptl/xhr';
import { Subscription } from 'rxjs';
import { SmallGroupSBCByGroupNumberRequest, SmallGroupSBCByGroupNumberResponse, Product } from './interfaces/search-by-group-number.interface';
import { SmallGroupSbcRequest } from './interfaces/SmallGroupSbcRequest.interface';
import { SmallGroupSBCItem } from './interfaces/SmallGroupSbcResponse.interface';
import { SmallGroupSbcService } from './services/small-group-sbc.service';

interface FormControlsAsJson {
  year: AbstractControl;
  region: AbstractControl;
  searchMethod: AbstractControl;
  groupNumber: AbstractControl;
  zipCode: AbstractControl;
  county: AbstractControl;
  countySingle: AbstractControl;
  effectiveDate: AbstractControl;
}
interface FormValuesAsJson {
  year: string;
  region: string;
  searchMethod: string;
  groupNumber: string;
  zipCode: string;
  county: string;
  countySingle: string;
  effectiveDate: string;
}
interface TextFilterFormControls {
  fileSearch: AbstractControl;
}
interface TextFilterFormControlsAsJson {
  fileSearch: string;
}

@Component({
    selector: 'app-small-group-sbc',
    templateUrl: './small-group-sbc.component.html',
    styleUrls: ['./small-group-sbc.component.scss'],
    standalone: false
})
export class SmallGroupSbcComponent implements OnInit {

  @Input() year1: string;
  @Input() year2: string;
  @Input() year3: string;

  sbcSearchForm: UntypedFormGroup;
  selectedSearchMethod: string = "groupNumber";
  counties: County[];
  isSingleCounty = true;
  singleCounty: County;
  formEl: ElementRef;
  showFilters: boolean;
  isDisabled = true;
  isMismatchedCounty = false;
  zipVal = "";
  onZipChange: Subscription;

  years = [];

  regions = [
    { name: "Western PA", value: 'WPA' },
    { name: "Central PA", value: 'CPA' },
    { name: "Northeastern PA", value: 'NEPA' },
    { name: "West Virginia", value: 'WVA' },
    { name: "Delaware", value: 'DE' },
    { name: "Western New York", value: 'WNY' },
    { name: "Northeastern New York", value: 'NENY' },
  ];

  months = [
    { name: "JANUARY", value: "01" },
    { name: "FEBRUARY", value: "02" },
    { name: "MARCH", value: "03" },
    { name: "APRIL", value: "04" },
    { name: "MAY", value: "05" },
    { name: "JUNE", value: "06" },
    { name: "JULY", value: "07" },
    { name: "AUGUST", value: "08" },
    { name: "SEPTEMBER", value: "09" },
    { name: "OCTOBER", value: "10" },
    { name: "NOVEMBER", value: "11" },
    { name: "DECEMBER", value: "12" },
  ];


  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("SmallGroupSbcPaginator") paginator: MatPaginator;

  dataSource: MatTableDataSource<any>;
  displayedColumns = [
    'productName',
    'download'
  ];

  textFilterForm: FormGroup;
  textFilterFormMinimumReqs = {
    length: 3
  }
  fileSearchSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private countyListService: CountyListService,
    public smallGroupSbcService: SmallGroupSbcService
  ) {
    this.sbcSearchForm = this.fb.group({
      year: this.fb.control("", Validators.required),
      region: this.fb.control("", Validators.required),
      searchMethod: this.fb.control("", Validators.required),
      groupNumber: this.fb.control("", Validators.required),
      zipCode: this.fb.control("", Validators.required),
      county: this.fb.control("", Validators.required),
      countySingle: this.fb.control("", Validators.required),
      effectiveDate: this.fb.control("", Validators.required)
    });
  }

  get formControls(): FormControlsAsJson {
    return this.sbcSearchForm.controls as unknown as FormControlsAsJson;
  }
  get formValues(): FormValuesAsJson {
    return this.sbcSearchForm.value as unknown as FormValuesAsJson;
  }
  get textFilterFormControls(): TextFilterFormControls {
    return this.textFilterForm.controls as unknown as TextFilterFormControls;
  }
  get textFilterFormValues(): TextFilterFormControlsAsJson {
    return this.textFilterForm.value as unknown as TextFilterFormControlsAsJson;
  }

  ngOnInit() {
    this.years = [
      this.year1,
      this.year2,
      this.year3
    ];

    this.onZipChange = this.sbcSearchForm.controls.zipCode.valueChanges.subscribe(async val => {
      
      this.sbcSearchForm.controls.county.reset();
      
      if (val?.length === 5) {
        await this.getCountyInfo(val);
        this.checkForPrimaryValues();
      } else {
        this.resetCounty();
      }
    });

    this.textFilterForm = this.fb.group({
      fileSearch: this.fb.control(null)
    });
    this.fileSearchSubscription = this.textFilterFormControls.fileSearch.valueChanges.subscribe(v => {
      if (v.length === 0 || v.length >= this.textFilterFormMinimumReqs.length) {
        this.textFilterForm.updateValueAndValidity({emitEvent: false, onlySelf: true});
        this.onFilterChange();
      }
    });

  }

  onChangeSearchMethod(event: MatRadioChange) {
    this.selectedSearchMethod = event.value;
    this.sbcSearchForm.controls.groupNumber.reset();
    this.sbcSearchForm.controls.zipCode.reset();
    this.sbcSearchForm.controls.county.reset();
    this.sbcSearchForm.controls.effectiveDate.reset();

    this.isDisabled = true;
  }

  private resetCounty() {
    this.sbcSearchForm.controls.county.setValue('');
    this.sbcSearchForm.controls.countySingle.setValue('');
    this.counties = [];
    this.isSingleCounty = true;
    this.checkForPrimaryValues();
  }

  async getCountyInfo(zipCode: string) {
    try {
      const response: NormalizedResponse<CountyListResponse> = await this.countyListService.getCountyInformation(zipCode);
      this.counties = response.payload.countyList;

      if (this.counties[0].region == this.sbcSearchForm.controls.region.value) {
        this.isMismatchedCounty = false;
        if (this.counties.length === 1) {
          this.isSingleCounty = true;
          console.log("we in here");
          this.sbcSearchForm.controls.countySingle.setValue(this.counties[0].county);
          this.countySelected(this.counties[0]);
          console.log(this.counties[0].county);
        }
        else if (this.counties.length > 1) {
          this.focusOnSelect();
          if (!this.sbcSearchForm.controls.county.disabled) {
            this.isSingleCounty = false;
          }
        }
      } else {
        this.isMismatchedCounty = true;
        this.sbcSearchForm.controls.zipCode.setErrors({ mismatchedZip: true })
        this.sbcSearchForm.controls.county.reset();
        console.log("regions don't match");
      }
    }
    catch (e) {

    }
  }

  onChangeMultipleCounties(event: MatSelectChange) {
    const countyData: County = event.value as County;
    this.countySelected(countyData);
  }

  countySelected(county: County) {
    this.singleCounty = county;
  }

  focusOnSelect() {
    setTimeout(() => {
      this.formEl?.nativeElement?.getElementsByClassName('multiple-counties-select')[0]?.focus();
    }, 100);
    this.checkForPrimaryValues();
  }

  async submitZipCodeSearch() {
    let monthVal: string;
    let countyCodeVal: string;

    let yearVal = this.sbcSearchForm.controls.year.value;

    if (this.selectedSearchMethod === 'zipCode') {
      monthVal = this.sbcSearchForm.controls.effectiveDate.value;
      countyCodeVal = this.singleCounty.code.toString();
    } else if (this.selectedSearchMethod === 'groupNumber') {
      monthVal = "01";
      countyCodeVal = "";
    }

    const payload: SmallGroupSbcRequest = {
      countycode: (() => {
        // Normalize to 3 characters
        while (countyCodeVal.length !== 3) { countyCodeVal = "0" + countyCodeVal }
        return countyCodeVal;
      })(),
      region: this.sbcSearchForm.controls.region.value,
      ddvalue: "",
      erisaIndicator: "Y",
      effectiveCovStartDate: monthVal + "/" + yearVal
    }

    const data: SmallGroupSBCItem[] = await this.smallGroupSbcService.getSmallGroupSbcs(payload);
    this.transformXHRDataToDataSource(data);
  }

  async submitGroupNumberSearch() {

    const sbcPostData: SmallGroupSBCByGroupNumberRequest = {
      "baseMessage": {
        "headerVersion": "1",
        "returnCode": null,
        "returnCodeDescription": null,
        "statusMsgs": null,
        "payloadVersion": "1"
      },
      "payload": {
        //group number search is only for small group plans
        "productType": "S",
        "groupNumber": this.formValues.groupNumber,
        "effectiveDate": "01/" + this.formValues.year,
        "effectiveCovStartDate": "",
        "erisaIndicator": "Y",
        "productIds": [""]
      }
    };

    const response: NormalizedResponse<SmallGroupSBCByGroupNumberResponse> = await this.smallGroupSbcService.groupNumberSearch(sbcPostData);
    response.payload.products.forEach(i => {
      i.hasNonErisaSbcDoc = false;
    });
    this.transformXHRDataToDataSource(response.payload.products);

  }

  onFieldChange() {
    this.checkForPrimaryValues();
  }

  onRegionChange() {
    this.sbcSearchForm.controls.zipCode.reset();
    this.zipVal = "";
    this.checkForPrimaryValues();
  }

  onGroupNumberChange(searchVal: string) {
    if (searchVal.length > 0) {
      this.checkForPrimaryValues();
    }
  }

  /** Ensure required fields for service payload are populated before enabling the submit button */
  checkForPrimaryValues() {
    if (this.sbcSearchForm.controls.region?.value && this.sbcSearchForm.controls.year?.value) {
      this.showFilters = true;
      if (this.selectedSearchMethod === "groupNumber") {
        if (this.sbcSearchForm.controls.groupNumber?.value?.length > 0) {
          this.isDisabled = false;
        } else {
          this.isDisabled = true;
        }
      } else if (this.selectedSearchMethod === "zipCode") {
        if (this.sbcSearchForm.controls.zipCode.value
          && (this.sbcSearchForm.controls.county.value
            || this.sbcSearchForm.controls.countySingle.value)
          && this.sbcSearchForm.controls.effectiveDate.value) {
          this.isDisabled = false
        } else {
          this.isDisabled = true;
        }
      }
    } else {
      this.showFilters = false;
    }
  }

  transformXHRDataToDataSource(xhrData: SmallGroupSBCItem[] | Product[]) {

    // Transform XHR service data to a generic MatTableDataSource
    this.dataSource = new MatTableDataSource<SmallGroupSBCItem>(xhrData);

    // Setup filter
    this.configureFilterPredicate();

    // Trigger filter
    this.onFilterChange();

    /** Setup Sort */
    this.dataSource.sort = this.sort;

    /** Setup Paginator */
    this.dataSource.paginator = this.paginator;

  }


  configureFilterPredicate() {
    if (this.dataSource) {

      console.log('configuring filter');

      this.dataSource.filterPredicate = (data: SmallGroupSBCItem | Product, filter: string) => {

        if (this.textFilterFormValues.fileSearch) {
          return data.productName?.toLocaleLowerCase()?.trim().includes(this.textFilterFormValues.fileSearch?.toLocaleLowerCase()?.trim());
        }
        return true;

      };

    }
  }

  /** Invoked from HTML */
  onFilterChange() {
    if (this.dataSource) {
      this.dataSource.filter = '';
      this.dataSource.filter = 'DO FILTER!';
    }
  }

  downloadDocument(document: SmallGroupSBCItem) {
    this.smallGroupSbcService.openSbc(document, false);
  }
  /** Zip-Code search, only */
  saveDocument(document: SmallGroupSBCItem) {
    window.open(document.link, '_blank');
  }
}
